import React, { useEffect, useMemo, useState, useCallback } from 'react';

// redux
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import animationData from '../lotties/empty-box';

// utils
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import ModalLarge from './Base/ModalLarge';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';

// MUI components
import {
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Divider,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  Button,
  StepConnector,
  stepConnectorClasses,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import { AccountTreeOutlined, AutoFixHighOutlined, CallToActionOutlined, DrawOutlined, NoteAddOutlined, StarOutlined, SyncOutlined } from '@mui/icons-material';
import Lottie from 'react-lottie';
import Select from '../stories/general-components/Select';
import { fetchBusinessData } from '../redux/actions-v2/coreAction';
import { toast } from 'react-toastify';
import LockOutlined from '@mui/icons-material/LockOutlined';


const StyledSearch = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
  },
  width: '100%',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'row',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '#f5f5f550',
  },
}));

const StyledCheckbox = styled(FormControlLabel)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(0.2),
  borderRadius: 0,
  transition: 'background-color 0.2s ease',
  '&:hover': (theme) => ({ // Use theme for hover styling
    backgroundColor: theme.palette.grey[100],
  }),
  '& .MuiCheckbox-root': {
    color: (theme) => theme.palette.text.secondary,
  },
  '& .Mui-checked': {
    color: (theme) => theme.palette.primary.main,
  },
}));



const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 15,
  },
  [`& .${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main, // Use theme color
    },
  },
  [`& .${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main, // Use theme color
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 10,
    border: 0,
    width: '80%',
    marginLeft: '10%',
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 10,
  },
}));



const ModalNewConnector = ({
  isOpen,
  modalCloseHandler,
  structureId,
  type
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const currentLangCode = i18n.language


  const [term, setTerm] = useState('');
  const [modules, setModules] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [configExtension, setConfigExtension] = useState(null);
  const [structureMatches, setStructureMatches] = useState({});
  const [fieldMatches, setFieldMatches] = useState({});


  const [accessCode, setAccessCode] = useState('');
  const [isCodeValidated, setIsCodeValidated] = useState(false);
  const [isCodeValidating, setIsCodeValidating] = useState(false);
  const [codeValidationError, setCodeValidationError] = useState('');
  const [installationId, setInstallationId] = useState(null);



  const businessStructure = useSelector(
    (state) => state.core.businessStructure
  );


  const businessPreference = useSelector((state) => state.core.businessData);
  const currentPlatform = useSelector(state => state.core.currentPlatform);
  const businessStructures = businessStructure?.structures;
  const structure = businessStructures.find((s) => s.id === structureId);

  const defaultLottieOptions = useMemo(
    () => ({
      loop: true,
      autoplay: true,
      animationData: animationData,
      rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
    }),
    []
  );

  const categories = [
    { value: 'feature', label: t('feature'), desc: 'Module, tableau de bord, rapport de Node' },
    { value: 'integration', label: t('integration'), desc: 'Données gérées par logiciel externe' },
    { value: 'plugin', label: t('plugin'), desc: 'Automatisme ou exécution automatique' },
    { value: 'account', label: t('account'), desc: 'Communication inter-comptes' },
    { value: 'component', label: t('component'), desc: 'Bloc, champ ou menu personnalisé' },
    { value: 'template', label: t('template'), desc: 'Courriel, PDF ou liste personnalisé' },
  ];


  const handleGetData = useCallback(async () => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/marketplace`,
        body: {
          query: debouncedTerm,  // Use debounced term here
          categories: selectedCategories,
        },
      });

      setModules(response || []);
    } catch (error) {
      console.error(error);
    }
  }, [debouncedTerm, selectedCategories, t]);

  const handleAddExtension = async () => {

    const structureData = selectedModule?.structures?.map(structure => {
      const matchOption = structureMatches[structure.id]?.matchOption || 'create';
      let backendStructureData = {
        name_fr: structure?.name_fr || structure?.name || '',
        collectionField: structure.collectionField,
        name_en: structure?.name_en || structure?.name || '',
        id: structure.id,
      };

      if (matchOption === 'match') {
        const existingStructureId = structureMatches[structure.id]?.selectedStructureId;
        if (existingStructureId) {
          backendStructureData = {
            matchExistingId: existingStructureId,
          };
        }
      }

      return backendStructureData; //  Return the data
    });
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/extensions`,
        body: {
          params: {
            permissions: configExtension?.permissions,
            structureId: configExtension?.structureId,
            action: configExtension?.action,
            openElement: configExtension?.openElement,
            structureMatches: structureMatches,
            structureData: structureData,
            fieldMatches: fieldMatches,
            query: configExtension?.query,
            location: configExtension?.location,
            limit: configExtension?.limit,
            language: configExtension?.language,
          },
          name: configExtension?.name,
          installationId: installationId || null,
          extensionId: selectedModule?.id,
          extensionDetails: selectedModule,
        },
      });
      if (response?.extensionId) {
        modalCloseHandler()
        dispatch(fetchBusinessData(businessPreference?.id, t, i18n.language, null, currentPlatform));
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedTerm(term);
    }, 300);


    return () => {
      clearTimeout(timeoutId);
    };
  }, [term]);

  useEffect(() => {
    handleGetData();
  }, [handleGetData]);


  const handleCategoryChange = (category) => {
    setSelectedCategories(prevSelected => {
      if (prevSelected.includes(category)) {
        return prevSelected.filter(c => c !== category);
      } else {
        return [...prevSelected, category];
      }
    });
  };

  const handleModuleClick = (module) => {
    if (module?.releaseDate) {
      const currentDate = new Date();
      const releaseDate = new Date(module?.releaseDate);
      if (releaseDate > currentDate) {
        toast.error(t('moduleNotReleased'));
        return;
      }
    }
    setSelectedModule(module);
    setConfigExtension({ permissions: [], structureId: null });
    // Reset validation state
    setAccessCode('');
    setCodeValidationError('');
    setIsCodeValidating(false);
    // Set validation status based on module requirement
    setIsCodeValidated(!module?.passwordRequired);
  };

  const handleTabChange = (event, newTab) => {
    setActiveTab(newTab);
  };

  // Function to validate the access code
  const handleValidateCode = async () => {
    if (!accessCode || !selectedModule?.objectID) return;

    setIsCodeValidating(true);
    setCodeValidationError('');

    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `business/codeExtension`,
        body: {
          extensionId: selectedModule.objectID,
          code: accessCode,
        },
      });

      if (response?.validated) {
        setIsCodeValidated(true);
        setInstallationId(response?.installationId);
      } else {
        setCodeValidationError(t('invalidAccessCode'));
        setIsCodeValidated(false);
        setInstallationId(null);
      }
    } catch (error) {
      console.error("Error validating code:", error);
      setCodeValidationError(t('errorValidatingCode'));
      setIsCodeValidated(false); // Ensure it's false on error
    } finally {
      setIsCodeValidating(false);
    }
  };

  const handleBackToMarketplace = () => {
    setSelectedModule(null);
    setCurrentStep(0);
    setActiveTab(0);
  };


  const passesStructureActions = businessStructures?.filter((struc) => struc?.collectionField === 'passes')?.map((structure) => (
    {
      label: t('create') + ' ' + (structure?.name)?.toLowerCase(),
      value: 'create-' + structure?.id,
      id: 'create-' + structure?.id,
    }
  )) || [];

  const steps = [
    {
      label: t('step1Title'), content: t('step1SubTitle'), restriction: 'all-values', config: (
        <div className='row'>

          <div className='mt-4 mb-2 col-6'>
            <TextField
              label={t('name')}
              value={configExtension?.name}
              helperText={t('nameHelperExt')}
              onChange={(e) => {
                setConfigExtension({ ...configExtension, name: e.target.value });
              }}
            />
          </div>

          {selectedModule?.requiredParams?.map((param, index) => (
            <div key={index} className='col-6'>
              {param?.id === 'structureId' && (
                <div className=''>
                  <Select
                    label={t('structureHook')}
                    value={configExtension?.structureId}
                    onChange={(e, value) => {
                      setConfigExtension({ ...configExtension, structureId: value });
                    }}
                    selections={
                      businessStructures?.map((structure) => ({
                        label: structure?.name,
                        value: structure?.id,
                        id: structure?.id,
                      })) || []
                    }
                  />
                </div>
              )}
              {param?.id === 'action' && (
                <div className=''>
                  <Select
                    label={t('actionToDo')}
                    value={configExtension?.action}
                    onChange={(e, value) => {
                      setConfigExtension({ ...configExtension, action: value });
                    }}
                    noEmpty
                    selections={
                      [
                        ...passesStructureActions,
                        {
                          label: t('createLog'),
                          value: 'createLog',
                          id: 'createLog',
                        },
                        {
                          label: t('setIsDone'),
                          value: 'setIsDone',
                          id: 'setIsDone',
                        },
                        {
                          label: t('updateStatus'),
                          value: 'updateStatus',
                          id: 'updateStatus',
                        }
                      ]
                    }
                  />
                </div>
              )}
              {param?.id === 'openElement' && (
                <div className=''>
                  <Select
                    label={t('openElement')}
                    value={configExtension?.action}
                    onChange={(e, value) => {
                      setConfigExtension({ ...configExtension, action: value });
                    }}
                    noEmpty
                    selections={
                      [
                        {
                          label: t('yes'),
                          value: true,
                          id: true,
                        },
                        {
                          label: t('no'),
                          value: false,
                          id: false,
                        },

                      ]
                    }
                  />
                </div>
              )}
              {param?.id === 'language' && (
                <div className=''>
                  <Select
                    label={t('language')}
                    value={configExtension?.language}
                    onChange={(e, value) => {
                      setConfigExtension({ ...configExtension, language: value });
                    }}
                    noEmpty
                    selections={
                      [
                        {
                          label: t('fr'),
                          value: 'fr',
                          id: 'fr',
                        },
                        {
                          label: t('en'),
                          value: 'en',
                          id: 'en',
                        },

                      ]
                    }
                  />
                </div>
              )}
              {param?.id === 'query' && (
                <div className='mt-4 mb-2 '>
                  <TextField
                    helperText={t('queryHelperExt')}
                    label={t('defaultQuery')}
                    value={configExtension?.query}
                    onChange={(e) => {
                      setConfigExtension({ ...configExtension, query: e.target.value });
                    }}
                  />
                </div>
              )}
              {param?.id === 'location' && (
                <div className='mt-4 mb-2 '>
                  <TextField
                    helperText={t('locationHelperExt')}
                    label={t('location')}
                    value={configExtension?.location}
                    onChange={(e) => {
                      setConfigExtension({ ...configExtension, location: e.target.value });
                    }}
                  />
                </div>
              )}
              {param?.id === 'limit' && (
                <div className='mt-4 mb-2 '>
                  <TextField
                    helperText={t('limitHelperExt')}
                    label={t('limit')}
                    value={configExtension?.limit}
                    type='number'
                    onChange={(e) => {
                      setConfigExtension({ ...configExtension, limit: e.target.value });
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )
    },
    {
      label: t('step2Title'), content: t('step2SubTitle'), restriction: 'none', config: (
        <div >
          {selectedModule?.structures?.length > 0 && (
            <>
              <Typography variant="h6" fontSize={'14px'} fontWeight={600} gutterBottom>{t('structureToAdd')}</Typography>
              <TableContainer elevation={0} component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '20%', m: 0, p: 0 }}>{t('name')}</TableCell>
                      <TableCell sx={{ width: '40%', m: 0, p: 0, paddingLeft: '10px' }}>{t('action')}</TableCell>
                      <TableCell sx={{ width: '40%', m: 0, p: 0, paddingLeft: '10px' }}>{t('structure')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedModule?.structures?.map((structure, index) => (
                      <TableRow sx={{ m: 0, p: 0 }} key={index}>
                        <TableCell sx={{ width: '20%', m: 0, p: 0 }}>{structure?.['name_' + currentLangCode] || structure?.name}</TableCell>
                        <TableCell sx={{ width: '40%', m: 0, p: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>

                            <Typography variant="body1" sx={{ ml: 1 }}>
                              {t('create')}
                            </Typography>
                            <Switch
                              checked={structureMatches[structure.id]?.matchOption === 'match'}
                              onChange={(e) => {
                                const isMatched = e.target.checked;
                                setStructureMatches(prev => ({
                                  ...prev,
                                  [structure.id]: {
                                    ...prev[structure.id],
                                    matchOption: isMatched ? 'match' : 'create',
                                    selectedStructureId: isMatched ? (prev[structure.id]?.selectedStructureId || '') : ''
                                  }
                                }));
                              }}
                              color="primary"
                            />
                            <Typography variant="body1" sx={{ mr: 1 }}>
                              {t('existing')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: '40%', m: 0, p: 0 }}>
                          {structureMatches[structure.id]?.matchOption === 'match' && (
                            <Select
                              noEmpty
                              label={t('select')}
                              value={structureMatches[structure.id]?.selectedStructureId || ''}
                              onChange={(e, value) => {
                                setStructureMatches(prev => ({
                                  ...prev,
                                  [structure.id]: {
                                    ...prev[structure.id],
                                    selectedStructureId: value,
                                  }
                                }));
                              }}
                              selections={businessStructures?.filter(existingStructure => existingStructure.collectionField === structure?.collectionField)?.map(structure => ({
                                label: structure?.name,
                                value: structure?.id,
                                id: structure?.id,
                              }))}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>)}
          {selectedModule?.fields?.custom?.length > 0 && (
            <>

              {Object.entries(
                selectedModule.fields.custom.reduce((acc, field) => {
                  const collection = field.matchedStructureCollection;
                  if (!acc[collection]) {
                    acc[collection] = [];
                  }
                  acc[collection].push(field);
                  return acc;
                }, {})
              ).map(([collectionField, fieldsInCollection]) => {
                const potentialTargetStructures = businessStructures?.filter(
                  (existingStructure) => existingStructure.collectionField === collectionField
                ) || [];


                const selectedTargetStructureId = fieldMatches[collectionField]?.selectedStructureId;
                const selectedTargetStructure = businessStructures?.find(s => s.id === selectedTargetStructureId);


                const structureSelectOptions = potentialTargetStructures.map(structure => ({
                  label: structure?.name,
                  value: structure?.id,
                  id: structure?.id,
                }));


                const selectedTargetFieldIdsInCollection = Object.values(fieldMatches[collectionField]?.fields || {})
                  .filter(match => match.matchOption === 'match' && match.selectedFieldId)
                  .map(match => match.selectedFieldId);

                return (
                  <Box key={collectionField} sx={{ mb: 3 }}>

                    {!selectedTargetStructureId && (
                      <Select
                        label={t('selectTargetStructure')}
                        value={selectedTargetStructureId || ''}
                        onChange={(e, value) => {
                          setFieldMatches(prev => ({
                            ...prev,
                            [collectionField]: {
                              ...prev[collectionField],
                              selectedStructureId: value,
                            }
                          }));
                        }}
                        selections={structureSelectOptions}
                        noEmpty
                        sx={{ mb: 2 }}
                      />
                    )}
                    {selectedTargetStructureId && (
                      <>
                        <Typography variant="h6" fontSize={'14px'} fontWeight={600} gutterBottom sx={{ mt: 3 }}>{t('fieldsToAdd')}</Typography>
                        <TableContainer elevation={0} component={Paper}>
                          <Table sx={{ minWidth: 650 }} aria-label={`field matching table for ${collectionField}`}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: '20%', m: 0, p: 0 }}>{t('name')}</TableCell>
                                <TableCell sx={{ width: '40%', m: 0, p: 0, paddingLeft: '10px' }}>{t('action')}</TableCell>
                                <TableCell sx={{ width: '40%', m: 0, p: 0, paddingLeft: '10px' }}>{t('matchToField')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {fieldsInCollection.map((field, index) => {
                                const currentFieldMatch = fieldMatches[collectionField]?.fields?.[field.externalSchema];
                                const isMatched = currentFieldMatch?.matchOption === 'match';
                                const selectedFieldId = currentFieldMatch?.selectedFieldId || '';

                                const fieldSelectOptions = selectedTargetStructure?.fields
                                  ?.filter(f => f.type !== 'layout' && f.typeData === field.typeData)
                                  ?.filter(targetField =>
                                    !selectedTargetFieldIdsInCollection.includes(targetField.id) || targetField.id === selectedFieldId
                                  )
                                  ?.map(targetField => ({
                                    label: targetField?.name || targetField?.['name_' + currentLangCode] || targetField?.name || targetField?.id, // Dynamic name
                                    value: targetField?.id,
                                    id: targetField?.id,
                                  })) || [];


                                return (
                                  <TableRow sx={{ m: 0, p: 0 }} key={field.id || index}>
                                    <TableCell sx={{ width: '20%', m: 0, p: 0 }}>{field?.['name_' + currentLangCode] || field?.name || field.externalSchema}</TableCell>
                                    <TableCell sx={{ width: '40%', m: 0, p: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body1" sx={{ ml: 1 }}>
                                          {t('create')}
                                        </Typography>
                                        <Switch
                                          checked={isMatched}
                                          disabled={fieldSelectOptions.length === 0}
                                          onChange={(e) => {
                                            const shouldMatch = e.target.checked;
                                            setFieldMatches(prev => ({
                                              ...prev,
                                              [collectionField]: {
                                                ...prev[collectionField],
                                                fields: {
                                                  ...prev[collectionField]?.fields,
                                                  [field.externalSchema]: {
                                                    ...prev[collectionField]?.fields?.[field.externalSchema],
                                                    matchOption: shouldMatch ? 'match' : 'create',
                                                    // Reset selected field ID if switching back to 'create'
                                                    selectedFieldId: shouldMatch ? (currentFieldMatch?.selectedFieldId || '') : ''
                                                  }
                                                }
                                              }
                                            }));
                                          }}
                                          color="primary"
                                        />
                                        <Typography variant="body1" sx={{ mr: 1 }}>
                                          {t('matchExisting')}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ width: '40%', m: 0, p: 0 }}>
                                      {isMatched && (
                                        <Select
                                          noEmpty // Or allow empty if needed
                                          label={t('selectField')}
                                          value={selectedFieldId}
                                          onChange={(e, value) => {
                                            setFieldMatches(prev => ({
                                              ...prev,
                                              [collectionField]: {
                                                ...prev[collectionField],
                                                fields: {
                                                  ...prev[collectionField]?.fields,
                                                  [field.externalSchema]: {
                                                    ...prev[collectionField]?.fields?.[field.externalSchema],
                                                    selectedFieldId: value,
                                                  }
                                                }
                                              }
                                            }));
                                          }}
                                          selections={fieldSelectOptions}
                                          // Disable if no target structure is selected or no fields available
                                          disabled={!selectedTargetStructureId || fieldSelectOptions.length === 0}
                                        />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Box>
                );
              })}
            </>)}
        </div>
      ),
    },
    {
      label: t('step3Title'), content: t('step3SubTitle'), restriction: 'all-checked', config: (
        <div style={{ maxHeight: '46vh', overflowY: 'scroll' }} className="row mt-3">
          {selectedModule?.permissions?.map((perm, index) => (
            <div className='col-6' key={index}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={configExtension?.permissions?.includes(perm)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setConfigExtension({
                          ...configExtension,
                          permissions: [...(configExtension?.permissions || []), perm],
                        });
                      } else {
                        setConfigExtension({
                          ...configExtension,
                          permissions: configExtension?.permissions?.filter((p) => p !== perm),
                        });
                      }
                    }}
                  />
                }
                label={
                  <React.Fragment>
                    <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                      {t(perm)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>
                      {t(`${perm}Description`)}
                    </Typography>
                  </React.Fragment>
                }
                key={index}
                sx={{
                  width: '100%',
                  padding: (theme) => theme.spacing(0.2),
                  borderRadius: 0,
                  transition: 'background-color 0.2s ease',
                  '&:hover': (theme) => ({
                    backgroundColor: theme.palette.grey[100],
                  }),
                  '& .MuiCheckbox-root': {
                    color: (theme) => theme.palette.text.secondary,
                  },
                  '& .Mui-checked': {
                    color: (theme) => theme.palette.primary.main,
                  },
                }}
              />
            </div>
          ))}
        </div>
      )
    },
    {
      label: t('step4Title'), content: t('step4SubTitle'), restriction: 'none', config: (
        <div style={{ maxHeight: '46vh', overflowY: 'scroll' }} className="row mt-3">
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('step4SummaryIntro')} <span style={{ fontWeight: 'bold' }}>{Object.entries(selectedModule?.type || {})
              .map(([key, value]) => value > 0 ? `${value} ${t(key)}(s) ${key === 'feature' ? t('inMenu') : ''}` : null)
              .filter(Boolean)
              .join(', ')}</span>
            . {t('step4SummaryConfirm')}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography fontWeight={600} variant="subtitle1" gutterBottom>{t('structures')}</Typography>
                {selectedModule?.structures?.map(structure => {
                  const matchInfo = structureMatches[structure.id];
                  const action = matchInfo?.matchOption === 'match' ? t('matchExtF') : t('createExtF');
                  const targetName = matchInfo?.matchOption === 'match'
                    ? businessStructures.find(s => s.id === matchInfo.selectedStructureId)?.name
                    : structure?.['name_' + currentLangCode] || structure?.name;
                  return (
                    <Typography key={structure.id} variant="body2" sx={{ mb: 0.5 }}>
                      - <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{structure?.['name_' + currentLangCode] || structure?.name}</span>: {action} {matchInfo?.matchOption === 'match' ? `(${t('with')} ${targetName})` : ''}
                    </Typography>
                  );
                })}
                {(!selectedModule?.structures || selectedModule.structures.length === 0) && (
                  <Typography variant="body2" color="text.secondary">{t('noStructuresInExt')}</Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>{t('fields')}</Typography>
                {Object.entries(fieldMatches).map(([collectionField, collectionMatch]) => (
                  <Box key={collectionField} sx={{ mb: 1 }}>
                    <Typography variant="caption" display="block" gutterBottom>{t('forCollection')}: {t(collectionField)} {t('targetStructure')}: {businessStructures.find(s => s.id === collectionMatch.selectedStructureId)?.name || t('notSelected')}</Typography>
                    {Object.entries(collectionMatch?.fields || {}).map(([externalSchema, fieldMatch]) => {
                      const originalField = selectedModule.fields.custom.find(f => f?.externalSchema === externalSchema && f?.matchedStructureCollection === collectionField);
                      const action = fieldMatch?.matchOption === 'match' ? t('matchExt') : t('createExt');
                      const targetStructure = businessStructures.find(s => s?.id === collectionMatch?.selectedStructureId);
                      const targetField = targetStructure?.fields.find(f => f?.id === fieldMatch?.selectedFieldId);
                      const targetFieldName = targetField?.name || targetField?.['name_' + currentLangCode] || targetField?.name_fr || targetField?.id;

                      return (
                        <Typography key={externalSchema} variant="body2" sx={{ mb: 0.5, ml: 1 }}>
                          - <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{originalField?.['name_' + currentLangCode] || originalField?.name_fr || externalSchema}</span>: {action} {fieldMatch?.matchOption === 'match' ? `${t('with')} ${targetFieldName || t('notSelected')}` : ''}
                        </Typography>
                      );
                    })}
                  </Box>
                ))}
                {(!selectedModule?.fields?.custom || selectedModule.fields.custom.length === 0) && (
                  <Typography variant="body2" color="text.secondary">{t('noFieldsInExt')}</Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: '12px', mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1">{t('basePriceOneTime')}:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {(selectedModule?.basePrice || 0).toLocaleString(i18n.language, { style: 'currency', currency: businessPreference?.currency || 'CAD' })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1">{t('monthlyFee')}:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {(selectedModule?.monthlyFee || 0).toLocaleString(i18n.language, { style: 'currency', currency: businessPreference?.currency || 'CAD' })}
                  </Typography>
                </Box>
                {selectedModule?.feePerElement && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="body1">{t('feePerElementExt')}:</Typography>
                    <Chip
                      label={`${(selectedModule?.perElementPrice || 0)} $ / ${selectedModule?.perElementPriceSuffix || t('element')}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  {t('pricingDisclaimer')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </div>
      )
    },
  ];

  const isNextButtonDisabled = useMemo(() => {
    // Validation for Step 2 (Field Matching)
    if (currentStep === 1) {
      for (const collectionKey in fieldMatches) {
        const collectionMatch = fieldMatches[collectionKey];
        if (collectionMatch.fields) {
          for (const fieldKey in collectionMatch.fields) {
            const fieldMatch = collectionMatch.fields[fieldKey];
            if (fieldMatch.matchOption === 'match' && !fieldMatch.selectedFieldId) {
              return true; // Disable if match is selected but no field is chosen
            }
          }
        }
      }
    }

    // Existing validation logic for other steps
    const currentStepData = steps[currentStep];
    if (!currentStepData) return false;

    switch (currentStepData.restriction) {
      case 'all-values':
        // Check if all required parameters have a non-empty value in configExtension
        return !selectedModule?.requiredParams?.every(param => {
          const value = configExtension?.[param.id];
          // Consider a value valid if it's not null, undefined, or an empty string
          // Adjust this check if specific params need different validation (e.g., booleans, numbers)
          return value !== undefined && value !== null && value !== '';
        });
      case 'all-checked':
        return !(selectedModule?.permissions?.every(perm => configExtension?.permissions?.includes(perm)));
      case 'none':
        return false;
      default:
        return false;
    }
  }, [currentStep, selectedModule, configExtension, fieldMatches]);

  const handleNext = () => {
    if (currentStep === steps.length - 1) {
      handleAddExtension();
    }

    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const StepIcon = ({ stepNumber }) => (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: currentStep >= stepNumber ? businessPreference?.mainColor : 'grey.300',
        display: 'flex',
        justifyContent: 'center',
        fontWeight: 600,
        alignItems: 'center',
        color: currentStep >= stepNumber ? '#FFFFFF' : '#696969',
        fontSize: '1.45rem',
      }}
    >
      {stepNumber + 1}
    </Box>
  );

  // Clear structureMatches when selectedModule changes
  useEffect(() => {
    setStructureMatches({});
  }, [selectedModule]);

  return (
    <ModalLarge
      isOpen={isOpen}
      subTitle={t('addNewConnectorDesc')}
      modalCloseHandler={modalCloseHandler}
      icon={structure?.icon}
      title={t('addConnector')}
      btnLabel={t('add')}
      fullWidth
      labels={{
        primary: currentStep === steps.length - 1 ? t('finish') : t('next'),
        secondary: t('back')
      }}
      disabledPrim={isNextButtonDisabled}
      handlePrim={selectedModule && handleNext}
      handleSec={currentStep === 0 ? null : handleBack}
      fullHeight={selectedModule ? true : false}
      maxWidth='lg'
    >
      <div style={{ justifyContent: 'flex-end', display: 'flex', width: '100%' }}>
        <Tabs value={activeTab} onChange={handleTabChange}
          sx={{
            height: '42px',
            maxHeight: '42px',
            minHeight: '42px',
            backgroundColor: isDarkMode ? '#000' : '#FFF',
            borderRadius: '40px',
            position: 'relative',
            overflow: 'hidden',
            '.MuiTabs-indicator': {
              display: 'none',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: '100%',
              width: '100%',
              borderRadius: '4px',
              transition: 'transform 0.3s ease-in-out',
              zIndex: 1,
            },
          }}>
          <Tab label={t('searchModules')} style={{
            fontWeight:
              (activeTab === 0)
                ? '600'
                : '500',
            textTransform: 'capitalize',
            padding: '10px 20px 10px 20px',
            fontSize: '13px',
          }} sx={{
            position: 'relative',
            zIndex: 2,
            minHeight: '42px',
            maxHeight: '42px',
            height: '42px',
            color: isDarkMode
              ? '#FFFFFF !important'
              : (activeTab !== 0)
                ? '#000000 !important'
                : '#FFFFFF !important',
            backgroundColor:
              (activeTab === 0)
                ? businessPreference?.mainColor
                : 'transparent',
            borderRadius: '30px',
            transition: 'color 0.3s ease-in-out',
            '&:hover': {
              color: '#FFFFFF',
            },
          }} />
          <Tab label={t('modulesActivated')} style={{
            fontWeight:
              (activeTab === 1)
                ? '600'
                : '500',
            textTransform: 'capitalize',
            padding: '10px 20px 10px 20px',
            fontSize: '13px',
          }} sx={{
            position: 'relative',
            zIndex: 2,
            minHeight: '42px',
            maxHeight: '42px',
            height: '42px',
            color: isDarkMode
              ? '#FFFFFF !important'
              : (activeTab !== 1)
                ? '#000000 !important'
                : '#FFFFFF !important',
            backgroundColor:
              (activeTab === 1)
                ? businessPreference?.mainColor
                : 'transparent',
            borderRadius: '30px',
            transition: 'color 0.3s ease-in-out',
            '&:hover': {
              color: '#FFFFFF',
            },
          }} />

        </Tabs>
      </div>

      {activeTab === 0 && !selectedModule && (
        <>
          <Grid container spacing={2} sx={{ paddingLeft: '20px', paddingRight: '20px' }}>
            <Grid item xs={3} sx={{ borderRadius: '8px', backgroundColor: '#00000008', padding: 2, }}>
              <Typography variant="h6" fontSize={'14px'} fontWeight={600} gutterBottom>{t('types')}</Typography>
              <Divider component="div" sx={{ mb: 2 }} />
              {categories?.map(category => (
                <StyledCheckbox
                  key={category?.value}
                  sx={{
                    backgroundColor: selectedCategories.includes(category?.value) ? businessPreference?.mainColor + '20' : 'transparent',
                  }}
                  control={
                    <Checkbox
                      checked={selectedCategories.includes(category?.value)}
                      onChange={() => handleCategoryChange(category?.value)}
                    />
                  }
                  label={category?.label}
                />
              ))}
            </Grid>

            <Grid item xs={9} sx={{ paddingLeft: '25px' }}>
              <Grid item xs={12} mt={2} mb={2}>
                <StyledSearch
                  placeholder={t('searchModule')}
                  variant="outlined"
                  sx={{
                    maxHeight: '42px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '40px',
                      backgroundColor: isDarkMode ? '#000' : '#FFF',
                    },
                    '& .MuiInputBase-input': {
                      padding: '10px 20px',
                    },
                  }}
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid container spacing={2} sx={{ height: '46vh', overflowY: 'scroll' }}>
                {modules?.length > 0 && modules?.map(module => (
                  <Grid item xs={12} sm={6} md={4} key={module.id}>
                    <StyledCard onClick={() => handleModuleClick(module)} elevation={0} sx={{ paddingLeft: 1.3 }}>
                      <CardMedia
                        component="img"
                        alt={module?.title}
                        image={module?.logoUrl || 'default_image.jpg'}
                        sx={{
                          mt: 2,
                          width: 30,
                          height: 30,
                          objectFit: 'contain',
                          alignSelf: 'flex-start',
                        }}
                      />
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" >
                          <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1rem', display: 'flex', alignItems: 'center' }}>
                            {module?.title}
                            {module?.passwordRequired && (
                              <LockOutlined sx={{ fontSize: '0.9rem', color: 'text.secondary', ml: 0.5 }} />
                            )}
                          </Typography>

                          {module?.releaseDate && module?.releaseDate > new Date() ? (
                            <Box
                              sx={{
                                backgroundColor: businessPreference?.mainColor + '40',
                                color: businessPreference?.mainColor,
                                px: 1,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                ml: 1,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {t('releaseSoon')}
                            </Box>
                          ) : module?.bestSeller ? (<Box
                            sx={{
                              backgroundColor: businessPreference?.mainColor + '40',
                              color: businessPreference?.mainColor,
                              px: 1,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              ml: 1,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {t('bestSeller')}
                          </Box>) : ('')}
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                          {module?.description}
                        </Typography>

                        <Divider component="div" sx={{ width: '100%', height: '1px', }} />

                        <Box display="flex" alignItems="center" gap={1} className='middle-content' mt={0.3}>

                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <StarOutlined sx={{ fontSize: '12px', paddingTop: '3px' }} /> {module?.rating || '0'} ({module?.votes || '0'})
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                            {t('by')} {module?.author || '-'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      {activeTab === 1 && !selectedModule && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            padding: '10px',
            textAlign: 'center',
          }}
        >
          <Lottie
            options={defaultLottieOptions}
            height={60}
            width={80}
          />
          <Typography
            variant="h6"
            sx={{ fontSize: '13px', mt: 1 }}
          >
            {t('emptyListTitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '10px',
              mt: 0.5,
              color: 'text.secondary',
            }}
          >
            {t('emptyListDescription')}
          </Typography>
        </div>
      )}


      {selectedModule && activeTab === 0 && (
        <div className='p-3'>

          {selectedModule?.passwordRequired && !isCodeValidated ? (

            <Box
              sx={{
                position: 'relative',
                height: '65vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                p: 3
              }}
            >
              <div className='mb-3 align-c'>
                <img src={selectedModule?.logoUrl || 'default_image.jpg'} alt={selectedModule?.title} style={{ width: '80px', height: '80px', objectFit: 'contain' }} />
              </div>

              <Typography variant="h5" fontWeight={600} gutterBottom>
                {t('accessCodeRequiredExt')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '510px' }}>
                {t('enterAccessCodePrompt')}
              </Typography>
              <TextField
                label={t('accessCodeLabel')}
                variant="outlined"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                sx={{ mb: 2, width: '100%', maxWidth: '300px' }}
                error={!!codeValidationError}
              />
              {codeValidationError && (
                <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: '300px' }}>
                  {codeValidationError}
                </Alert>
              )}
              <Button
                variant="contained"
                sx={{ textTransform: 'none' }}
                onClick={handleValidateCode}
                disabled={!accessCode || isCodeValidating}
                startIcon={isCodeValidating ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {t('validateCodeButton')}
              </Button>
              <Typography variant="body2" onClick={handleBackToMarketplace} sx={{ cursor: 'pointer', mt: 3 }}>
                {t('cancelInstall')}
              </Typography>
            </Box>
          ) : (
            <div className='row'>
              <div className='col-3' style={{ backgroundColor: '#F9F9F9', height: '65vh', borderRadius: '8px', padding: '15px' }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: selectedModule?.moduleColor || theme.palette.grey[300], // Use moduleColor or fallback
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}>
                  <CardMedia
                    component="img"
                    alt={selectedModule?.title}
                    image={selectedModule?.logoUrl || 'default_image.jpg'}
                    sx={{
                      width: 30,
                      height: 30,
                      objectFit: 'contain',
                      filter: 'brightness(0) invert(1)'
                    }}
                  />
                </Box>
                <Typography variant="h6" fontSize={'16px'} fontWeight={600} gutterBottom>{selectedModule?.title}</Typography>
                <Typography variant="body1" fontSize={'12px'} fontWeight={400} color='#69696999' lineHeight={1.1} gutterBottom>{selectedModule?.description}</Typography>
                <div className='mt-5'>
                  <Typography variant="h6" fontSize={'12px'} fontWeight={600} gutterBottom>{t('pricing')}</Typography>
                  {selectedModule?.basePrice ? (
                    <Typography variant="caption" display="block" sx={{ mb: 0.2 }}>
                      {t('basePriceShort')}: {(selectedModule?.basePrice || 0).toLocaleString(i18n.language, { style: 'currency', currency: businessPreference?.currency || 'CAD' })}
                    </Typography>
                  ) : ('')}
                  {selectedModule?.monthlyFee ? (
                    <Typography variant="caption" display="block" sx={{ mb: 0.2 }}>
                      {t('monthlyFeeShort')}: {(selectedModule?.monthlyFee || 0)?.toLocaleString(i18n.language, { style: 'currency', currency: businessPreference?.currency || 'CAD' })}
                    </Typography>
                  ) : ('')}
                  {selectedModule?.feePerElement ? (
                    <Typography variant="caption" display="block" sx={{ mb: 0.2 }}>
                      {t('perElementShort')}: {(selectedModule?.perElementPrice || 0)?.toLocaleString(i18n.language, { style: 'currency', currency: businessPreference?.currency || 'CAD' })} / {selectedModule?.perElementPriceSuffix || t('element')}
                    </Typography>
                  ) : ('')}
                </div>
                <div className='mt-4'>
                  <Typography variant="h6" fontSize={'12px'} fontWeight={600} gutterBottom>{t('withThatExt')}</Typography>
                  <div className='row'>
                    {selectedModule?.type?.feature && (
                      <div className='d-flex middle-content'>
                        <div><NoteAddOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                        <div>
                          <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.feature + ' ' + t('module') + '(s)'}</Typography>
                        </div>
                      </div>
                    )}
                    {selectedModule?.type?.component && (
                      <div className='d-flex middle-content'>
                        <div><CallToActionOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                        <div>
                          <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.component + ' ' + t('component') + '(s)'}</Typography>
                        </div>
                      </div>
                    )}
                    {selectedModule?.type?.integration && (
                      <div className='d-flex middle-content'>
                        <div><SyncOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                        <div>
                          <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.integration + ' ' + t('integrationExt') + '(s)'}</Typography>
                        </div>
                      </div>)}
                    {selectedModule?.type?.template && (
                      <div className='d-flex middle-content'>
                        <div><DrawOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                        <div>
                          <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.template + ' ' + t('template') + '(s)'}</Typography>
                        </div>
                      </div>)}
                    {selectedModule?.type?.plugin && (
                      <div className='d-flex middle-content'>
                        <div><AutoFixHighOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                        <div>
                          <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.plugin + ' ' + t('automation') + '(s)'}</Typography>
                        </div>
                      </div>)}
                    {selectedModule?.type?.account && (
                      <div className='d-flex middle-content'>
                        <div><AccountTreeOutlined sx={{ fontSize: '15px', marginRight: '8px' }} /></div>
                        <div>
                          <Typography variant="h6" fontSize={'11px'} sx={{ marginTop: '2px' }} fontWeight={400} gutterBottom>{selectedModule?.type?.account + ' ' + t('account') + '(s)'}</Typography>
                        </div>
                      </div>)}
                  </div>
                </div>
                <div className='mt-3'>
                  <Typography variant="p" sx={{ cursor: 'pointer' }} onClick={handleBackToMarketplace} fontSize={'10px'} fontWeight={400} gutterBottom>{t('cancelInstall')}</Typography>
                </div>
              </div>
              {/* Stepper and Step Content (Right Side) */}
              <div className='col-9 py-3'>
                <Stepper activeStep={currentStep} alternativeLabel
                  connector={<ColorlibConnector />}
                >
                  {steps?.map((step, index) => (
                    <Step key={step?.label}>
                      <StepLabel
                        StepIconComponent={() => <StepIcon stepNumber={index} />}
                      >
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <div>
                  <div className='px-5 mt-4'>
                    <div>
                      <Typography variant="h6" fontSize={'18px'} fontWeight={600} gutterBottom>{steps[currentStep]?.label}</Typography>
                      <div style={{ marginTop: '-8px', maxWidth: '400px' }}>
                        <Typography variant="caption" fontSize={'11px'} lineHeight={1.05} fontWeight={400} color='#69696999'>{steps[currentStep]?.content}</Typography>
                      </div>
                      <div className='mt-2' style={{ minHeight: '46vh', maxHeight: '46vh', paddingBottom: '10px', overflowY: 'scroll' }}>
                        {steps[currentStep]?.config}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </ModalLarge>
  );
};

export default ModalNewConnector;